/* W17 M1/M4 — activation/désactivation des Systems + profondeur progressive.
   N'édite QUE systems_enabled / system_depth sur l'organisation : aucune donnée
   touchée, le monde canonique reste intact (réactivation = contexte retrouvé). */
import { useEffect, useState } from "react";
import { Hexagon, Loader2 } from "lucide-react";
import { api } from "@/lib/api";
import { notify, fmtError } from "@/lib/notify";
import { SYSTEM_DEFS } from "@/lib/systems";

const DEPTH_LABELS = {
  deal: ["L1 · Opportunity Awareness", "L2 · Interaction commerciale", "L3 · Transaction Management", "L4 · Deal Economics", "L5 · Transactions complexes"],
  financial_markets: ["L1 · Market Awareness", "L2 · Market Exposure", "L3 · Market Intelligence", "L4 · Financial Decision", "L5 · Market Execution"],
  governance: ["G1 · Essentiel", "G2 · Maîtrise", "G3 · Assurance"],
};

export const SystemsCard = () => {
  const [state, setState] = useState(null);
  const [busy, setBusy] = useState("");

  const load = () => api.get("/workspace/systems").then((r) => setState(r.data)).catch(() => setState(false));
  useEffect(() => { load(); }, []);

  const patch = async (key, payload) => {
    setBusy(key);
    try {
      const r = await api.patch("/workspace/systems", payload);
      setState((s) => ({ ...s, systems: r.data.systems, depth: r.data.depth }));
      window.dispatchEvent(new CustomEvent("noam:org-updated"));
      notify.success("Systems mis à jour. Aucune donnée modifiée — le monde canonique reste intact.");
    } catch (e) {
      notify.error(fmtError(e, "Modification réservée aux administrateurs."));
      load();
    } finally {
      setBusy("");
    }
  };

  const toggle = (id) => {
    const on = state.systems.includes(id);
    const next = on ? state.systems.filter((s) => s !== id) : [...state.systems, id];
    patch(id, { systems_enabled: next });
  };

  if (state === false) return null;
  if (!state) {
    return (
      <div className="noam-card p-6 text-sm text-zinc-400 flex items-center gap-2" data-testid="systems-card-loading">
        <Loader2 className="w-4 h-4 animate-spin" /> Chargement des Systems…
      </div>
    );
  }

  return (
    <div className="noam-card p-6 space-y-4" data-testid="systems-card">
      <div>
        <div className="flex items-center gap-2 text-[10px] font-semibold tracking-[0.18em] uppercase text-[#378ADD]">
          <Hexagon className="w-3 h-3" /> Systems &amp; profondeur
        </div>
        <p className="text-xs text-zinc-400 mt-1.5">
          Un monde unique, trois Systems activables. Désactiver un System masque son interface
          sans jamais toucher aux données : la réactivation restitue le contexte intact.
        </p>
      </div>
      {SYSTEM_DEFS.map((def) => {
        const enabled = state.systems.includes(def.id);
        const levels = DEPTH_LABELS[def.id];
        const cur = state.depth?.[def.id] ?? levels.length;
        return (
          <div key={def.id} className={`p-3 rounded border ${enabled ? "border-white/10 bg-white/[0.02]" : "border-white/5 bg-black/20 opacity-70"}`} data-testid={`systems-card-row-${def.id}`}>
            <div className="flex items-center gap-3">
              <def.icon className="w-4 h-4 shrink-0" style={{ color: enabled ? def.accent : undefined }} />
              <div className="flex-1 min-w-0">
                <div className="text-sm font-medium text-white">{def.label}</div>
                <div className="text-[11px] text-zinc-500 truncate">{def.description}</div>
              </div>
              <select
                value={cur}
                disabled={!enabled || busy !== ""}
                onChange={(e) => patch(`depth-${def.id}`, { depth: { [def.id]: Number(e.target.value) } })}
                className="bg-black/30 border border-white/10 rounded-md px-2 py-1.5 text-xs text-zinc-200 focus:border-[#378ADD] focus:outline-none disabled:opacity-40"
                data-testid={`systems-depth-select-${def.id}`}
              >
                {levels.map((label, i) => (
                  <option key={i} value={i + 1}>{label}</option>
                ))}
              </select>
              <label className="inline-flex items-center gap-2 cursor-pointer select-none">
                <input
                  type="checkbox"
                  checked={enabled}
                  disabled={busy !== ""}
                  onChange={() => toggle(def.id)}
                  className="accent-[#378ADD] w-4 h-4"
                  data-testid={`systems-toggle-${def.id}`}
                />
                <span className={`text-xs ${enabled ? "text-[#1D9E75]" : "text-zinc-500"}`}>{enabled ? "Activé" : "Désactivé"}</span>
              </label>
            </div>
          </div>
        );
      })}
      {busy && (
        <div className="text-xs text-zinc-500 flex items-center gap-2" data-testid="systems-card-busy">
          <Loader2 className="w-3 h-3 animate-spin" /> Application…
        </div>
      )}
    </div>
  );
};

export default SystemsCard;
